import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Link } from "react-router-dom";
import {
  Trophy,
  Star,
  Medal,
  Crown,
  Lightning,
  Shield,
  SpinnerGap,
  ArrowUp,
  User
} from "@phosphor-icons/react";

import { API_BASE as API } from "../lib/apiBase";

const LEVEL_COLORS = [
  "from-zinc-400 to-zinc-500",
  "from-blue-400 to-blue-600",
  "from-teal-400 to-teal-600",
  "from-amber-400 to-amber-600",
  "from-purple-400 to-purple-600",
  "from-rose-400 to-rose-600",
];

export default function LeaderboardPage() {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [gamification, setGamification] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [lbRes, gmRes] = await Promise.all([
        axios.get(`${API}/api/leaderboard`, { withCredentials: true }),
        axios.get(`${API}/api/user/gamification`, { withCredentials: true })
      ]);
      setLeaderboard(lbRes.data);
      setGamification(gmRes.data);
    } catch (err) {
      console.error("Failed to load leaderboard:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SpinnerGap size={32} className="animate-spin text-[#002FA7]" />
      </div>
    );
  }

  const BADGE_ICONS = {
    video: "fa-video",
    compass: "fa-compass",
    graduation: "fa-graduation-cap",
    trophy: "fa-trophy",
    chat: "fa-comments",
    star: "fa-star",
    flag: "fa-flag",
    shield: "fa-shield-halved",
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl mx-auto">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight font-['Outfit']" data-testid="leaderboard-title">
          Classement & Progression
        </h1>
        <p className="text-zinc-500 mt-1">Suivez votre progression et comparez-vous a l'equipe</p>
      </div>

      {/* My Stats Card */}
      {gamification && (
        <div className="bg-gradient-to-br from-[#001845] to-[#002FA7] rounded-xl p-6 text-white" data-testid="my-gamification-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-blue-200">Mon profil</p>
              <h2 className="text-xl font-bold font-['Outfit']">{user?.name}</h2>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold font-['Outfit']">{gamification.total_points}</div>
              <p className="text-xs text-blue-200">points</p>
            </div>
          </div>

          {/* Level Progress */}
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm mb-1.5">
              <span className="flex items-center gap-1.5">
                <Shield size={16} weight="fill" />
                Niveau {gamification.level}
              </span>
              <span className="text-blue-200">{gamification.level_progress}% vers Niv. {gamification.level + 1}</span>
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all duration-700"
                style={{ width: `${gamification.level_progress}%` }}
              />
            </div>
          </div>

          {/* Badges */}
          <div>
            <p className="text-xs text-blue-200 mb-2">{gamification.badges_earned.length}/{gamification.total_badges} badges obtenus</p>
            <div className="flex flex-wrap gap-2">
              {gamification.badges_earned.map((badge) => (
                <div
                  key={badge.id}
                  className="flex items-center gap-1.5 px-2.5 py-1 bg-white/15 rounded-full text-xs font-medium"
                  title={badge.description}
                  data-testid={`badge-${badge.id}`}
                >
                  <i className={`fa-solid ${BADGE_ICONS[badge.icon] || "fa-medal"} text-amber-300`} />
                  {badge.name}
                </div>
              ))}
              {gamification.badges_earned.length === 0 && (
                <span className="text-xs text-blue-300">Continuez pour debloquer des badges !</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center gap-2">
          <Trophy size={20} className="text-amber-500" />
          <h2 className="font-medium font-['Outfit']">Classement de l'equipe</h2>
        </div>
        <div>
          {leaderboard.length > 0 ? (
            leaderboard.map((entry, idx) => {
              const rank = idx + 1;
              const levelColor = LEVEL_COLORS[Math.min(entry.level - 1, LEVEL_COLORS.length - 1)];
              return (
                <div
                  key={entry.user_id}
                  className={`flex items-center gap-4 px-6 py-4 border-b border-zinc-50 transition-colors ${
                    entry.is_current_user ? "bg-blue-50/50" : "hover:bg-zinc-50"
                  }`}
                  data-testid={`leaderboard-entry-${idx}`}
                >
                  {/* Rank */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                    rank === 1 ? "bg-amber-100 text-amber-700" :
                    rank === 2 ? "bg-zinc-200 text-zinc-600" :
                    rank === 3 ? "bg-orange-100 text-orange-700" :
                    "bg-zinc-100 text-zinc-500"
                  }`}>
                    {rank <= 3 ? (
                      rank === 1 ? <Crown size={16} weight="fill" /> :
                      rank === 2 ? <Medal size={16} weight="fill" /> :
                      <Medal size={16} />
                    ) : rank}
                  </div>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium text-sm ${entry.is_current_user ? "text-[#002FA7]" : ""}`}>
                        {entry.name}
                      </span>
                      {entry.display_role && (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-700">
                          {entry.display_role}
                        </span>
                      )}
                      {entry.is_current_user && (
                        <span className="text-[10px] text-[#002FA7] font-medium">(vous)</span>
                      )}
                    </div>
                    {entry.team && <p className="text-xs text-zinc-400">{entry.team}</p>}
                  </div>

                  {/* Level */}
                  <div className={`px-2.5 py-1 rounded-full bg-gradient-to-r ${levelColor} text-white text-xs font-bold`}>
                    Niv. {entry.level}
                  </div>

                  {/* Points */}
                  <div className="text-right">
                    <div className="font-semibold text-sm font-['Outfit']">{entry.total_points}</div>
                    <div className="text-[10px] text-zinc-400">pts</div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="p-8 text-center text-zinc-400 text-sm">
              <Trophy size={36} className="mx-auto mb-3 opacity-30" />
              <p>Aucune donnée de classement encore</p>
              <p className="text-xs mt-1">Regardez des videos et completez des quiz pour gagner des points !</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
